import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { MapOverlay } from '@/types/models';
import { ONE } from '@/constants/common';
import {
  addOverlay,
  getAllPublicOverlaysByProjectId,
  getAllProjectOverlays,
  getAllUserOverlaysByCreator,
  getOverlay,
  removeOverlay,
  setOverlayOwner,
  updateOverlays,
  updateSingleOverlay,
} from './overlays.thunks';
import type { OverlaysState } from './overlays.types';

export const getAllPublicOverlaysByProjectIdReducer = (
  builder: ActionReducerMapBuilder<OverlaysState>,
): void => {
  builder.addCase(getAllPublicOverlaysByProjectId.pending, state => {
    state.loading = 'pending';
  });
  builder.addCase(getAllPublicOverlaysByProjectId.fulfilled, (state, action) => {
    state.data = action.payload || [];
    state.loading = 'succeeded';
  });
  builder.addCase(getAllPublicOverlaysByProjectId.rejected, state => {
    state.loading = 'failed';
    // TODO to discuss manage state of failure
  });
};

export const addOverlayReducer = (builder: ActionReducerMapBuilder<OverlaysState>): void => {
  builder.addCase(addOverlay.pending, state => {
    state.addOverlay.loading = 'pending';
  });
  builder.addCase(addOverlay.fulfilled, state => {
    state.addOverlay.loading = 'succeeded';
  });
  builder.addCase(addOverlay.rejected, state => {
    state.addOverlay.loading = 'failed';
    // TODO to discuss manage state of failure
  });
};

export const getAllUserOverlaysByCreatorReducer = (
  builder: ActionReducerMapBuilder<OverlaysState>,
): void => {
  builder.addCase(getAllUserOverlaysByCreator.pending, state => {
    state.userOverlays.loading = 'pending';
  });
  builder.addCase(getAllUserOverlaysByCreator.fulfilled, (state, action) => {
    state.userOverlays.data = action.payload;
    state.userOverlays.loading = 'succeeded';
  });
  builder.addCase(getAllUserOverlaysByCreator.rejected, state => {
    state.userOverlays.loading = 'failed';
    // TODO to discuss manage state of failure
  });
};

export const updateOverlaysReducer = (builder: ActionReducerMapBuilder<OverlaysState>): void => {
  builder.addCase(updateOverlays.pending, state => {
    state.updateOverlays.loading = 'pending';
  });
  builder.addCase(updateOverlays.fulfilled, state => {
    state.updateOverlays.loading = 'succeeded';
  });
  builder.addCase(updateOverlays.rejected, state => {
    state.updateOverlays.loading = 'failed';
    // TODO to discuss manage state of failure
  });
};

export const removeOverlayReducer = (builder: ActionReducerMapBuilder<OverlaysState>): void => {
  builder.addCase(removeOverlay.pending, state => {
    state.removeOverlay.loading = 'pending';
  });
  builder.addCase(removeOverlay.fulfilled, state => {
    state.removeOverlay.loading = 'succeeded';
  });
  builder.addCase(removeOverlay.rejected, state => {
    state.removeOverlay.loading = 'failed';
    // TODO to discuss manage state of failure
  });
};

export const getAllProjectOverlaysReducer = (
  builder: ActionReducerMapBuilder<OverlaysState>,
): void => {
  builder.addCase(getAllProjectOverlays.pending, state => {
    state.projectOverlays.loading = 'pending';
    state.projectOverlays.error = { name: '', message: '' };
  });
  builder.addCase(getAllProjectOverlays.fulfilled, (state, action) => {
    state.projectOverlays.data = action.payload || [];
    state.projectOverlays.loading = 'succeeded';
    state.projectOverlays.error = { name: '', message: '' };
  });
  builder.addCase(getAllProjectOverlays.rejected, (state, action) => {
    state.projectOverlays.loading = 'failed';
    state.projectOverlays.error = action.error as Error;
  });
};

export const updateSingleOverlayReducer = (
  builder: ActionReducerMapBuilder<OverlaysState>,
): void => {
  builder.addCase(updateSingleOverlay.pending, state => {
    state.updateSingleOverlay.loading = 'pending';
  });
  builder.addCase(updateSingleOverlay.fulfilled, (state, action) => {
    state.updateSingleOverlay.loading = 'succeeded';
    // Update the overlay in projectOverlays data if it exists
    if (state.projectOverlays.data) {
      const index = state.projectOverlays.data.findIndex(
        overlay => overlay.id === action.payload.id,
      );
      const NOT_FOUND = -1;
      if (index !== NOT_FOUND) {
        state.projectOverlays.data[index] = action.payload;
      }
    }
  });
  builder.addCase(updateSingleOverlay.rejected, state => {
    state.updateSingleOverlay.loading = 'failed';
    // TODO to discuss manage state of failure
  });
};

export const setOverlayOwnerReducer = (builder: ActionReducerMapBuilder<OverlaysState>): void => {
  builder.addCase(setOverlayOwner.pending, state => {
    state.updateSingleOverlay.loading = 'pending';
  });
  builder.addCase(setOverlayOwner.fulfilled, (state, action) => {
    state.updateSingleOverlay.loading = 'succeeded';
    if (state.projectOverlays.data) {
      const index = state.projectOverlays.data.findIndex(
        overlay => overlay.id === action.payload.id,
      );
      const NOT_FOUND = -1;
      if (index !== NOT_FOUND) {
        state.projectOverlays.data[index] = action.payload;
      }
    }
  });
  builder.addCase(setOverlayOwner.rejected, state => {
    state.updateSingleOverlay.loading = 'failed';
  });
};

export const getOverlayReducer = (builder: ActionReducerMapBuilder<OverlaysState>): void => {
  builder.addCase(getOverlay.fulfilled, (state, action) => {
    if (!action.payload) {
      return;
    }
    const overlay = action.payload;
    const { id } = overlay;
    const updateIn = (list: MapOverlay[] | [] | undefined): void => {
      if (!list) return;
      const mutableList = list as MapOverlay[];
      const index = mutableList.findIndex(o => o.id === id);
      const NOT_FOUND = -1;
      if (index !== NOT_FOUND) {
        mutableList[index] = overlay;
      } else {
        mutableList.push(overlay);
      }
    };
    if (overlay.publicOverlay) {
      updateIn(state.projectOverlays.data);
    } else {
      updateIn(state.userOverlays.data);
    }
  });
};

export const deleteOverlayReducer = (
  state: OverlaysState,
  action: PayloadAction<{ overlayId: number }>,
): void => {
  const { overlayId } = action.payload;
  const removeFrom = (list: typeof state.data): void => {
    if (!list) return;
    const index = list.findIndex(o => o.id === overlayId);
    const NOT_FOUND = -1;
    if (index !== NOT_FOUND) {
      list.splice(index, ONE);
    }
  };
  removeFrom(state.projectOverlays.data);
  removeFrom(state.userOverlays.data);
};
